import React from "react";
import { commitMutation, graphql } from "react-relay";

import cx from "classnames";

import SettingsPageHeader from "~/account/SettingsPageHeader";
import useToggle from "~/lib/hooks/useToggle";
import { openWindowCentered } from "~/lib/popup";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { environment } from "~/models";
import { Button } from "~/ui";
import BetaLabel from "~/ui/labels/BetaLabel";
import Checkbox from "~/ui/legacy/Checkbox";
import { ButtonLink, Link } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import type { MeetingAuditGiveConsentMutation } from "~/__generated__/MeetingAuditGiveConsentMutation.graphql";
import type { MeetingAuditQuery, MeetingAuditQuery$data } from "~/__generated__/MeetingAuditQuery.graphql";
import type { MeetingAuditStartMutation } from "~/__generated__/MeetingAuditStartMutation.graphql";

import styles from "./MeetingAudit.module.css";

type Integrations = MeetingAuditQuery$data["viewer"]["account"]["integrations"];

const STEPS = ["intro", "install", "confirm", "complete"];

export const MEETING_AUDIT_FLAG = "meeting-audit";

const Query = graphql`
	query MeetingAuditQuery {
		viewer {
			account {
				hasMeetingAuditLog
				integrations {
					gsuite {
						id
						delegated
					}
					office365 {
						id
						adminConsented
						signedAccountIdUrl
					}
				}
			}
		}
	}
`;

const GiveConsentMutation = graphql`
	mutation MeetingAuditGiveConsentMutation {
		giveMeetingAuditConsent {
			gsuite {
				id
				delegated
			}
			office365 {
				id
				adminConsented
			}
		}
	}
`;

const StartMutation = graphql`
	mutation MeetingAuditStartMutation {
		startMeetingAudit {
			account {
				hasMeetingAuditLog
			}
		}
	}
`;

function IntroStep({ onClickNextStep }: { onClickNextStep: () => void }) {
	return (
		<>
			<h1>Meeting Audit</h1>
			<p>
				Transform your organization&apos;s meeting culture with Fellow&apos;s meeting audit. The meeting audit will
				analyze your organization&apos;s calendar and provide you with data-driven insights and actionable
				recommendations to optimize your organization&apos;s meeting practices.
			</p>

			<Button size="normal" variant="primary" onClick={onClickNextStep}>
				Get started
			</Button>
		</>
	);
}

function InstallStep({
	onClickNextStep,
	office365,
	gsuite,
}: {
	onClickNextStep: () => void;
	office365: Integrations["office365"];
	gsuite: Integrations["gsuite"];
}) {
	const [isInstalled, toggleIsInstalled] = useToggle(false);

	const installText = `Fellow from ${gsuite ? "the Google Workspace Marketplace" : office365 ? "Microsoft 365" : ""}`;
	const configureURL = (gsuite ? window.GOOGLE_MARKETPLACE_LINK : office365 ? office365.signedAccountIdUrl : "") ?? "";

	const onClickOfficeAuth = React.useCallback(() => {
		// Simpler version of promptOAuth(...)
		openWindowCentered(configureURL, "oauth", 480, 720);
	}, [configureURL]);

	const configureLink = gsuite ? (
		<Link rel="noopener noreferrer" target="_blank" href={configureURL}>
			{installText}
		</Link>
	) : office365 ? (
		<ButtonLink onClick={onClickOfficeAuth}>{installText}</ButtonLink>
	) : null;

	const onClick = React.useCallback(() => {
		if (!isInstalled) return;

		commitMutation<MeetingAuditGiveConsentMutation>(environment, {
			mutation: GiveConsentMutation,
			variables: {},
			onCompleted: ({ giveMeetingAuditConsent }, errors) => {
				if (giveMeetingAuditConsent?.gsuite || giveMeetingAuditConsent?.office365) {
					onClickNextStep();
				} else {
					const message = gsuite
						? "Fellow was not successfully installed from the Google Workspace Marketplace."
						: office365
							? "Fellow was unable to connect to Microsoft 365."
							: "We were unable to connect";

					toast.error(errors?.[0]?.message || message, { title: "Installation error" });
				}
			},
		});
	}, [onClickNextStep, isInstalled, gsuite, office365]);

	return (
		<>
			<h1>Install {installText}</h1>
			<p>To get started, install {configureLink}.</p>
			<p>
				<Checkbox id="checkbox" label={installText} checked={isInstalled} onChange={toggleIsInstalled} />
				<label htmlFor="checkbox" className={styles.checkboxLabel}>
					I have installed {installText}.
				</label>
			</p>
			<Button size="normal" variant="primary" onClick={onClick} disabled={!isInstalled}>
				Proceed to next step
			</Button>
		</>
	);
}

function ConfirmStep({ onClickNextStep }: { onClickNextStep: () => void }) {
	const onClick = React.useCallback(() => {
		commitMutation<MeetingAuditStartMutation>(environment, {
			mutation: StartMutation,
			variables: {},
			onCompleted: ({ startMeetingAudit }, errors) => {
				if (startMeetingAudit) {
					onClickNextStep();
				} else {
					toast.error(errors?.[0]?.message || "We were unable to start the meeting audit", { title: "Error" });
				}
			},
		});
	}, [onClickNextStep]);

	return (
		<>
			<h1>Confirm</h1>
			<p>You&apos;re almost done! Confirm to start your meeting audit analysis.</p>
			<p>
				By continuing you agree that Fellow will be analyzing your organization&apos;s calendar data and that the
				calendar event descriptions will be processed by OpenAI. OpenAI will not store your data.
			</p>
			<Button size="normal" variant="primary" onClick={onClick}>
				Start analysis
			</Button>
		</>
	);
}

function CompleteStep() {
	return (
		<>
			<h1>Meeting Audit Analytics in progress</h1>
			<p>
				Your meeting audit analysis is underway! Your Fellow representative will be in touch within the next week with
				your meeting audit report.
			</p>
		</>
	);
}

export default function MeetingAudit() {
	const [step, setStep] = React.useState(STEPS[0]);

	const queryData = useLazyLoadQuery<MeetingAuditQuery>(Query, {}, {});
	const { gsuite, office365 } = queryData.viewer.account.integrations;
	const hasMeetingAuditLog = queryData.viewer.account.hasMeetingAuditLog;

	const isConnected = gsuite ? gsuite.delegated : office365 ? office365.adminConsented : false;
	const subject = (
		<>
			Meeting Audit&ensp;
			<BetaLabel />
			&ensp;
			<span className={cx(styles.label, { [styles.connected]: isConnected })}>
				{isConnected ? "Connected" : "Disconnected"}
			</span>
		</>
	);

	const jumpToInstallStep = React.useCallback(() => setStep("install"), []);
	const jumpToConfirmStep = React.useCallback(() => setStep("confirm"), []);
	const jumpToCompleteStep = React.useCallback(() => setStep("complete"), []);

	return (
		<div>
			<SettingsPageHeader subject={subject} actions={[]} />

			<div className={styles.container}>
				{step === "intro" && (
					<IntroStep
						onClickNextStep={
							hasMeetingAuditLog ? jumpToCompleteStep : isConnected ? jumpToConfirmStep : jumpToInstallStep
						}
					/>
				)}

				{step === "install" && (
					<InstallStep onClickNextStep={jumpToConfirmStep} office365={office365} gsuite={gsuite} />
				)}

				{step === "confirm" && <ConfirmStep onClickNextStep={jumpToCompleteStep} />}

				{step === "complete" && <CompleteStep />}
			</div>
		</div>
	);
}
